import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { X, ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { Organization } from "@/hooks/useOrganizations";

interface OrganizationsMultiSelectProps {
  organizations: Organization[];
  selectedOrganizations: string[];
  onSelectionChange: (selectedIds: string[]) => void;
}

const OrganizationsMultiSelect = ({
  organizations,
  selectedOrganizations,
  onSelectionChange,
}: OrganizationsMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const toggleOrganizationById = (orgId: string) => {
    const newSelection = selectedOrganizations.includes(orgId)
      ? selectedOrganizations.filter((id) => id !== orgId)
      : [...selectedOrganizations, orgId];

    onSelectionChange(newSelection);
  };

  const clearSelection = () => {
    onSelectionChange([]);
    setOpen(false);
  };

  const filteredOrganizations = useMemo(() => {
    if (!searchTerm) return organizations;
    
    return organizations.filter((org) =>
      org.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [organizations, searchTerm]);

  const getDisplayText = () => {
    if (selectedOrganizations.length === 0) {
      return "Todas las organizaciones";
    }
    if (selectedOrganizations.length === 1) {
      const org = organizations.find((o) => o.id === selectedOrganizations[0]);
      return org?.name || "1 seleccionada";
    }
    return `${selectedOrganizations.length} seleccionadas`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className="justify-between min-w-[200px] rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          <span className="truncate">{getDisplayText()}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <div className="p-2 border-b">
          <Input
            placeholder="Buscar organizaciones..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-8"
          />
        </div>

        <div className="max-h-[300px] overflow-auto">
          {filteredOrganizations.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              No se encontraron organizaciones
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {filteredOrganizations.map((org) => (
                <div
                  key={org.id}
                  className="flex items-center space-x-2 p-2 rounded hover:bg-accent cursor-pointer"
                  onClick={() => toggleOrganizationById(org.id)}
                >
                  <Checkbox
                    checked={selectedOrganizations.includes(org.id)}
                    onCheckedChange={() => toggleOrganizationById(org.id)}
                  />
                  <span className="text-sm flex-1">{org.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedOrganizations.length > 0 && (
          <div className="p-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              className="w-full justify-start text-muted-foreground"
            >
              <X className="mr-2 h-4 w-4" />
              Limpiar selección
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default OrganizationsMultiSelect;
